import * as ToggleGroup from "@radix-ui/react-toggle-group";
import { LoadingContainer } from "~/app/[course]/loader";
import { useCourseContext } from "~/contexts/CourseContext";
import { useUserContext } from "~/contexts/UserContext";
import { useSidebar } from "~/hooks/useSidebar";
import { api } from "~/utils/api";
import { formatMoney, formatTime } from "~/utils/formatters";
import { googleAnalyticsEvent } from "~/utils/googleAnalyticsUtils";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type Dispatch,
  type SetStateAction,
} from "react";
import { toast } from "react-toastify";
import { Avatar } from "../avatar";
import { FilledButton } from "../buttons/filled-button";
import { OutlineButton } from "../buttons/outline-button";
import { Item } from "../course-page/filters";
import { DownChevron } from "../icons/down-chevron";
import { Info } from "../icons/info";
import { InlineAlert } from "../inline-alert/inline-alert";
import { Players } from "../icons/players";
import { Tooltip } from "../tooltip";
import { type OwnedTeeTime } from "./owned";
import { SingleSlider } from "../input/single-slider";
import Flyout from "../modal/flyout";
import { Modal } from "../modal/modal";
import { useMediaQuery } from "usehooks-ts";
import type { SaleTypeOption } from "../input/sale-type-select";
import { SaleTypeSelector } from "../input/sale-type-select";
import { useAppContext } from "~/contexts/AppContext";

type PlayerType = "1" | "2" | "3" | "4";

const PlayerOptions = ["1", "2", "3", "4"];


export const SPLIT_TYPE_OPTIONS: SaleTypeOption[] = [
  {
    value: "split",
    caption: "Allow Sale in Splits (Recommended)",
    description: "Buyers can purchase any number of the rounds you list. (There is a risk that partial rounds can go unsold)",
    tooltip: "Increases the pool of potential buyers as they can choose the exact number of rounds they want.",
  },
  {
    value: "whole",
    caption: "Only Sell in Whole",
    description: "Buyers must purchase all the listed rounds together from the seller. (This may limit potential buyers)",
    tooltip: "Ensures the entire set of rounds is sold at once, useful for group bookings. Eliminates risk seller left with partial bought.",
  },
];

const DEFAULT_SELL_TYPE = "whole"

// Mirrors NotResellableReason in booking.service.ts — canResell can be 0 for
// reasons other than membership, so pick the message that matches the actual cause.
const NOT_RESELLABLE_MESSAGES: Record<string, string> = {
  membership: "Not allowed to sell because this booking is part of a membership.",
  phoneBooking: "Not allowed to sell because this is a phone booking.",
  offerPurchase: "Not allowed to sell because this booking was purchased through an accepted offer.",
  unpaid: "Not allowed to sell because this booking hasn't been fully paid yet.",
};
const DEFAULT_NOT_RESELLABLE_MESSAGE = "This booking isn't eligible for resale.";

export const getNotResellableMessage = (reason?: string | null) =>
  (reason && NOT_RESELLABLE_MESSAGES[reason]) ?? DEFAULT_NOT_RESELLABLE_MESSAGE;

type SideBarProps = {
  isListTeeTimeOpen: boolean;
  setIsListTeeTimeOpen: Dispatch<SetStateAction<boolean>>;
  selectedTeeTime: OwnedTeeTime | undefined;
  refetch: () => Promise<unknown>;
  needsRedirect?: boolean;
};

type ListTeeTimeDetailsProps = SideBarProps

export const ListTeeTime = ({
  isListTeeTimeOpen,
  setIsListTeeTimeOpen,
  selectedTeeTime,
  refetch,
  needsRedirect,
}: SideBarProps) => {

  const isMobile = useMediaQuery("(max-width: 768px)");

  return isMobile ? (
    <Modal
      title="Sell"
      isOpen={isListTeeTimeOpen}
      onClose={() => setIsListTeeTimeOpen(false)}
    >
      <ListTeeTimeDetail
        isListTeeTimeOpen={isListTeeTimeOpen}
        setIsListTeeTimeOpen={setIsListTeeTimeOpen}
        selectedTeeTime={selectedTeeTime}
        refetch={refetch}
        needsRedirect={needsRedirect}
      />
    </Modal>
  ) : (
    <Flyout
      title="Sell"
      isOpen={isListTeeTimeOpen}
      setIsOpen={() => setIsListTeeTimeOpen(false)}
    >
      <ListTeeTimeDetail
        isListTeeTimeOpen={isListTeeTimeOpen}
        setIsListTeeTimeOpen={setIsListTeeTimeOpen}
        selectedTeeTime={selectedTeeTime}
        refetch={refetch}
        needsRedirect={needsRedirect}
      />
    </Flyout>
  );
};

const ListTeeTimeDetail = ({
  isListTeeTimeOpen,
  setIsListTeeTimeOpen,
  selectedTeeTime,
  refetch,
  needsRedirect,
}: ListTeeTimeDetailsProps) => {
  const availableSlots = selectedTeeTime?.golfers.length || 0;
  const { entity } = useAppContext();
  const [listingPrice, setListingPrice] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sellerServiceFee, setSellerServiceFee] = useState<number>(0);
  const [players, setPlayers] = useState<string>(
    selectedTeeTime?.isGroupBooking ?
      "2" :
      selectedTeeTime?.selectedSlotsCount || availableSlots.toString()
  );
  const [saleType, setSaleType] = useState<string>(DEFAULT_SELL_TYPE);
  useSidebar({
    isOpen: isListTeeTimeOpen,
    setIsOpen: setIsListTeeTimeOpen,
  });
  const sell = api.teeBox.createListingForBookings.useMutation();
  const sellGroup = api.teeBox.createListingForGroupBookings.useMutation();
  const canResell = api.teeBox.checkIfUserIsOptMemberShip.useMutation();
  const router = useRouter();
  const { course } = useCourseContext();
  const courseId = course?.id;
  const { user } = useUserContext();
  const auditLog = api.webhooks.auditLog.useMutation();
  const logAudit = async () => {
    await auditLog.mutateAsync({
      userId: user?.id ?? "",
      teeTimeId: selectedTeeTime?.teeTimeId ?? "",
      bookingId: selectedTeeTime?.bookingIds?.[0] ?? "",
      listingId: selectedTeeTime?.listingId ?? "",
      courseId,
      eventId: "TEE_TIME_LISTED",
      json: `TEE_TIME_LISTED`,
    });
  };
  const listingSellerFeePercentage = (course?.sellerFee ?? 1) / 100;
  const listingBuyerFeePercentage = (course?.buyerFee ?? 1) / 100;

  const maxListingPrice = useMemo(() => {
    if (!selectedTeeTime?.purchasedFor) return 0;
    const max =
      selectedTeeTime?.purchasedFor *
      (1 +
        (course?.maxListPricePerGolferPercentage
          ? course?.maxListPricePerGolferPercentage / 100
          : 0));

    return Math.round(parseInt(max.toFixed(2)));
  }, [selectedTeeTime]);

  useEffect(() => {
    const slots = selectedTeeTime?.golfers.length || 0;
    setPlayers(
      selectedTeeTime?.isGroupBooking ? "2" :
        selectedTeeTime?.selectedSlotsCount || slots.toString()
    );
  }, [selectedTeeTime?.selectedSlotsCount, selectedTeeTime?.golfers]);

  useEffect(() => {
    if (isListTeeTimeOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
      setListingPrice(0); //reset price
      // setPlayers(selectedTeeTime?.selectedSlotsCount||"1");
      setErrorMessage(null);
    }
    setSaleType(DEFAULT_SELL_TYPE);
  }, [isListTeeTimeOpen]);

  const handleFocus = () => {
    if (!listingPrice) setListingPrice(0);
  };

  const handleBlur = () => {
    if (!listingPrice) setListingPrice(0);
  };

  const handleListingPrice = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[$,]/g, "");

    // if( value.indexOf("-1") > -1) return;

    const decimals = value.split(".")[1];
    if (decimals && decimals?.length > 2) return;

    const strippedLeadingZeros = value.replace(/^0+/, "");
    setListingPrice(Number(strippedLeadingZeros));
  };

  const handleSingleSliderChange = (value: number[]) => {
    if (value[0]) {
      setPlayers(value[0].toString());
    } else {
      setErrorMessage("Error selecting number of players");
    }
  };

  const totalPayout = useMemo(() => {
    if (!listingPrice) {
      setSellerServiceFee(0);
      return 0;
    }
    if (listingPrice <= 0) {
      setSellerServiceFee(0);
      return 0;
    }

    const sellerListingPricePerGolfer = parseFloat(listingPrice.toString());

    const buyerListingPricePerGolfer =
      sellerListingPricePerGolfer * (1 + listingBuyerFeePercentage);
    const sellerFeePerGolfer =
      sellerListingPricePerGolfer * listingSellerFeePercentage;
    const buyerFeePerGolfer =
      sellerListingPricePerGolfer * listingBuyerFeePercentage;
    let totalPayoutForAllGolfers =
      (buyerListingPricePerGolfer - buyerFeePerGolfer - sellerFeePerGolfer) *
      parseInt(players);

    // totalPayoutForAllGolfers =
    //   ( totalPayoutForAllGolfers <= 0 ? 0 : totalPayoutForAllGolfers );

    totalPayoutForAllGolfers =
      (totalPayoutForAllGolfers <= 0 ? 0 : totalPayoutForAllGolfers) +
      (selectedTeeTime?.weatherGuaranteeAmount ?? 0) / 100 +
      (selectedTeeTime?.totalMerchandiseAmount ?? 0) / 100;

    // setSellerServiceFee(
    //   sellerFeePerGolfer * parseInt(players) +
    //     (selectedTeeTime?.weatherGuaranteeAmount ?? 0) / 100
    // );

    setSellerServiceFee(sellerFeePerGolfer * parseInt(players));

    return Math.abs(totalPayoutForAllGolfers);
  }, [listingPrice, players]);

  const getSaleTypeParams = (saleType: string) => {
    switch (saleType) {
      case "split":
        return {
          allowSplit: true,
        }
      case "whole":
        return {
          allowSplit: false,
        }
      default:
        return {
          allowSplit: false,
        }
    }
  }

  const listTeeTime = async () => {
    googleAnalyticsEvent({ action: "btn_mtb_list_submit" });
    setIsLoading(true);
    setErrorMessage(null);
    void logAudit();

    try {
      const canResellResult = await canResell.mutateAsync({
        bookingId: selectedTeeTime?.bookingIds[0] ?? "",
      });
      if (canResellResult.canResell === 0) {
        setErrorMessage(getNotResellableMessage(canResellResult.notResellableReason));
        setIsLoading(false);
        return;
      }
    } catch (error) {
      setIsLoading(false);
      setErrorMessage((error as Error)?.message ?? "Database error");
    }

    //You should never enter this condition.
    if (totalPayout < 0) {
      setErrorMessage("Listing price must be greater than $45.");
      setIsLoading(false);
      return;
    }
    if (!selectedTeeTime) {
      setErrorMessage("Invalid date on tee time.");
      setIsLoading(false);
      return;
    }

    if (listingPrice > maxListingPrice) {
      setErrorMessage(
        `Listing price cannot be greater than ${formatMoney(maxListingPrice)}.`
      );
      setIsLoading(false);
      return;
    }
    if (listingPrice === 0) {
      setErrorMessage(`Enter listing price.`);
      setIsLoading(false);
      return;
    }
    if (listingPrice === 1) {
      setErrorMessage(`Listing price must be greater than $1.`);
      setIsLoading(false);
      return;
    }
    try {
      if (selectedTeeTime?.isGroupBooking) {
        await sellGroup.mutateAsync({
          groupId: selectedTeeTime?.groupId,
          listPrice: listingPrice,
          endTime: new Date(selectedTeeTime?.date),
          slots: parseInt(players),
          color1: entity?.color1,
        })
      } else {
        const saleTypeParams = getSaleTypeParams(saleType)
        await sell.mutateAsync({
          bookingIds: selectedTeeTime?.bookingIds.slice(0, parseInt(players)),
          listPrice: listingPrice,
          endTime: new Date(selectedTeeTime?.date),
          slots: parseInt(players),
          color1: entity?.color1,
          ...saleTypeParams
        });
      }
      toast.success(
        <div className="flex flex-col ">
          <div>Your tee time has been listed.</div>
          <Link
            href={`?section=my-listed-tee-times`}
            className="flex w-fit items-center gap-1 text-primary" prefetch={false}
          >
            <div>View listed tee times</div>
            <DownChevron fill={"var(--primary-color)"} className="w-[0.875rem] -rotate-90" />
          </Link>
        </div>
        , {
          progressStyle: {
            background: "var(--primary-color)",
          },
        });
      if (needsRedirect) {
        return router.push(
          `/${selectedTeeTime?.courseId}/my-tee-box?section=my-listed-tee-times`
        );
      }
      setIsLoading(false);
      await refetch();
      setIsListTeeTimeOpen(false);
    } catch (error) {
      setErrorMessage(
        (error as Error)?.message ?? "An error occurred selling tee time."
      );
    } finally {
      setIsLoading(false);
    }
  };

  console.log("selectedTeeTime", selectedTeeTime);

  return (<>
    {isLoading && <LoadingContainer isLoading={isLoading}>
      <div></div>
    </LoadingContainer>}
    <div className="relative flex h-full flex-col justify-between overflow-y-auto">
      <div className="flex flex-col gap-6 px-0 sm:px-4 mb-6">
        <TeeTimeItem
          courseImage={selectedTeeTime?.courseLogo ?? ""}
          courseName={selectedTeeTime?.courseName ?? ""}
          courseDate={selectedTeeTime?.date ?? ""}
          golferCount={selectedTeeTime?.golfers.length ?? 0}
          timezoneCorrection={course?.timezoneCorrection}
        />
        <div className={`flex flex-col gap-1 text-center w-fit mx-auto`}>
          <label
            htmlFor="listingPrice"
            className="text-base text-primary-gray md:text-lg"
          >
            Enter listing price per golfer
          </label>
          <div className="relative">
            <span className="absolute left-1 top-1 text-2xl md:text-4xl">
              $
            </span>
            <input
              id="listingPrice"
              value={listingPrice
                ?.toString()
                ?.replace(/^0+/, "")
                .replace(".", "")
                .replace("-", "")}
              type="number"
              onFocus={handleFocus}
              onChange={handleListingPrice}
              onBlur={handleBlur}
              className="mx-auto max-w-[18.75rem] rounded-lg bg-secondary-white px-4 py-1 text-center text-2xl font-semibold outline-none md:text-4xl pl-6"
              data-testid="listing-price-id"
            />
          </div>
        </div>

        <div className={`flex  flex-col gap-1 text-center`}>
          <label
            htmlFor="spots"
            className="text-base text-primary-gray md:text-lg"
          >
            Select number of spots to list
          </label>
          {!selectedTeeTime?.isGroupBooking ?
            <ToggleGroup.Root
              id="spots"
              type="single"
              value={players}
              onValueChange={(player: PlayerType) => {
                if (availableSlots < parseInt(player)) return;
                if (player) setPlayers(player);
              }}
              orientation="horizontal"
              className="mx-auto flex"
              data-testid="player-button-id"
            >
              {PlayerOptions.map((value, index) => (
                <Item
                  key={index}
                  dataTestId="player-item-id"
                  dataQa={value}
                  value={value}
                  className={`${index === 0
                    ? "rounded-l-full border border-stroke"
                    : index === PlayerOptions.length - 1
                      ? "rounded-r-full border-b border-t border-r border-stroke"
                      : "border-b border-r border-t border-stroke"
                    } px-[1.75rem] ${availableSlots < index + 1
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                    }`}
                  label={value}
                />
              ))}
            </ToggleGroup.Root>
            :
            <SingleSlider
              min={1}
              max={selectedTeeTime?.golfers.length || 0}
              step={1}
              onValueChange={(value) => handleSingleSliderChange(value)}
              aria-label="Select number of players"
              data-testid="slider-number-of-players"
              value={[Number(players)]}
              shouldDisplayValue={true}
            />}
        </div>
        <div className="text-justify bg-secondary-white">
          If you purchased weather protection, you will receive a full
          refund. Any remaining owned rounds for this time will be subject
          to raincheck policy.
        </div>
        {!selectedTeeTime?.isGroupBooking
          ? <SaleTypeSelector
            className="flex flex-col w-full"
            value={saleType}
            onValueChange={setSaleType}
            saleTypeOptions={SPLIT_TYPE_OPTIONS}
            defaultValue={saleType}
          />
          : null
        }
      </div>
      <div className="flex flex-col gap-4 px-4 pb-6">
        <div className="flex justify-between">
          <div className="font-[300] text-primary-gray">
            Your Listing Price{" "}
            <Tooltip
              trigger={<Info className="h-[0.875rem] w-[0.875rem]" />}
              content={
                <div className="max-w-[13.75rem] text-sm break-words">
                  Buyer sees a slightly higher amount. These buyer/seller fees help keep the lights on at Golf District and to continuously provide better service.
                </div>
              }
            />
          </div>
          <div className="text-secondary-black">
            {formatMoney(listingPrice * Number(players))}
          </div>
        </div>
        <div className="flex justify-between">
          <div className="font-[300] text-primary-gray">
            Service Fee{" "}
            <Tooltip
              trigger={<Info className="h-[0.875rem] w-[0.875rem]" />}
              content={
                <div className="max-w-[12.5rem] text-sm break-words">
                  This fee ensures ongoing enhancements to our service, ultimately offering golfers the best access to booking tee times
                </div>
              }
            />
          </div>
          <div className="text-secondary-black">
            ({formatMoney(sellerServiceFee)})
          </div>
        </div>
        <div className="flex justify-between">
          <div className="font-[300] text-primary-gray">
            Weather Guarantee Refund{" "}
            <Tooltip
              trigger={<Info className="h-[0.875rem] w-[0.875rem]" />}
              content="Weather guarantee amount to be refunded"
            />
          </div>
          <div className="text-secondary-black">
            {formatMoney((selectedTeeTime?.weatherGuaranteeAmount ?? 0) / 100)}
          </div>
        </div>
        {course?.supportsSellingMerchandise || (selectedTeeTime?.totalMerchandiseAmount ?? 0) > 0 ? <div className="flex justify-between">
          <div className="font-[300] text-primary-gray">
            Merchandise Purchase Refund{" "}
            <Tooltip
              trigger={<Info className="h-[0.875rem] w-[0.875rem]" />}
              content="Merchandise Purchase amount to be refunded"
            />
          </div>
          <div className="text-secondary-black">
            {formatMoney(
              (selectedTeeTime?.totalMerchandiseAmount ?? 0) / 100
            )}
          </div>
        </div> : null}
        <div className="flex justify-between">
          <div className="font-[300] text-primary-gray">
            You Receive after Sale
          </div>
          <div className="text-secondary-black">
            {formatMoney(totalPayout)}
          </div>
        </div>
        <div className="text-center text-sm font-[300] text-primary-gray">
          All sales are final.
        </div>
        {errorMessage && <InlineAlert status="error">{errorMessage}</InlineAlert>}
        <div className="flex flex-col gap-2">
          <FilledButton
            className="w-full"
            onClick={() => void listTeeTime()}
            data-testid="sell-tee-time-button-id"
          >
            Sell
          </FilledButton>

          <OutlineButton
            onClick={() => setIsListTeeTimeOpen(false)}
            data-testid="cancel-button-id"
          >
            Cancel
          </OutlineButton>
        </div>
      </div>
    </div>
  </>);
};

const TeeTimeItem = ({
  courseImage,
  courseName,
  courseDate,
  golferCount,
  timezoneCorrection,
}: {
  courseImage: string;
  courseName: string;
  courseDate: string;
  golferCount: number;
  timezoneCorrection: number | undefined;
}) => {
  return (
    <div className="flex flex-col gap-2 rounded-xl bg-secondary-white px-4 py-5">
      <div className="flex items-center gap-4">
        <Avatar src={courseImage} />
        <div className="flex flex-col">
          <div className="whitespace-normal overflow-y-auto text-secondary-black">
            {courseName}
          </div>
          <div className="text-primary-gray">
            {formatTime(courseDate, false, timezoneCorrection, true)}
          </div>
        </div>
      </div>
      <div className="flex gap-4 text-sm">
        <div className="w-[2.5rem] ">
          <Players className="ml-auto w-[1.875rem]" />
        </div>
        {golferCount} {golferCount === 1 ? "golfer" : "golfers"}
      </div>
    </div>
  );
};